/**
 * Super-admin Users directory + drill-down profile.
 *
 * /admin/users            → paginated list with role & search filter.
 * /admin/users/:id        → full profile: identity, wallet, reports, referrals, recharges, family.
 */
/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, User, Wallet, FileText, Gift, IndianRupee, ArrowLeft, Loader2, Building2 } from "lucide-react";

const ROLES = [
  { id: "all", label: "All" },
  { id: "patient", label: "Patients" },
  { id: "lab_admin", label: "Lab Admins" },
  { id: "super_admin", label: "Super Admins" },
];

export function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (role !== "all") params.set("role", role);
    params.set("limit", "100");
    api.get(`/admin/users?${params.toString()}`)
      .then((r) => { setRows(r.data.users || []); setTotal(r.data.total || 0); })
      .catch((e) => toast.error(apiError(e, "Could not load users")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [role]);

  return (
    <DashboardLayout role="super_admin">
      <div className="max-w-6xl">
        <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2">
          <Users className="w-6 h-6 text-efgh-blue" /> User directory
        </h1>
        <p className="text-ink/60 text-sm mt-1">Search, filter and drill into every EFGH user — patients, lab admins, super admins.</p>

        <Card className="mt-6 p-4 rounded-2xl flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or phone…" onKeyDown={(e) => { if (e.key === "Enter") load(); }} />
          </div>
          <Select value={role} onValueChange={(v) => setRole(v)}>
            <SelectTrigger className="w-[180px]" data-testid="users-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={load} data-testid="users-search-btn" className="bg-ink text-white">Search</Button>
          <div className="text-xs text-ink/50 ml-auto">Showing {rows.length} of {total}</div>
        </Card>

        <Card className="mt-4 p-0 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-ink/50">No users match your filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="text-[10px] uppercase tracking-widest text-ink/50 font-heading bg-muted/40">
                  <tr>
                    <th className="text-left px-4 py-3">User</th>
                    <th className="text-left px-4 py-3">Role</th>
                    <th className="text-left px-4 py-3">Referral</th>
                    <th className="text-left px-4 py-3">Joined</th>
                    <th className="px-2 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-ink">{u.full_name || "—"}</div>
                        <div className="text-[11px] text-ink/50">{u.email} {u.phone ? `· ${u.phone}` : ""}</div>
                      </td>
                      <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                      <td className="px-4 py-3 font-mono text-xs">{u.referral_code || "—"}</td>
                      <td className="px-4 py-3 text-xs text-ink/60">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="px-2 py-3 text-right">
                        <Link to={`/admin/users/${u.id}`}><Button size="sm" variant="outline" data-testid={`view-user-${u.id}`}>Open</Button></Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}

function RoleBadge({ role }) {
  const map = {
    patient: { label: "Patient", cls: "bg-efgh-blue/10 text-efgh-blue" },
    lab_admin: { label: "Lab admin", cls: "bg-efgh-orange/10 text-efgh-orange" },
    super_admin: { label: "Super admin", cls: "bg-danger/10 text-danger" },
  };
  const s = map[role] || { label: role || "—", cls: "bg-muted text-ink/70" };
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-semibold ${s.cls}`}>{s.label}</span>;
}

// ───────────────────────────────────────────────────────────────
// Individual user profile
// ───────────────────────────────────────────────────────────────
export function AdminUserProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoAmt, setPromoAmt] = useState("");
  const [promoNote, setPromoNote] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  const load = () => api.get(`/admin/users/${id}`).then((r) => setData(r.data));
  useEffect(() => {
    load().catch((e) => toast.error(apiError(e, "Could not load profile"))).finally(() => setLoading(false));
  }, [id]);

  const grantPromo = async (sign) => {
    const n = parseInt(promoAmt, 10);
    if (!n || n <= 0) { toast.error("Enter a positive coin amount"); return; }
    setPromoBusy(true);
    try {
      await api.post("/admin/wallet/adjust", {
        user_id: id,
        coins: sign * n,
        note: promoNote || (sign > 0 ? "Promotional credit" : "Promotional debit"),
      });
      toast.success(sign > 0 ? `Credited ${n} coins` : `Debited ${n} coins`);
      setPromoAmt(""); setPromoNote("");
      await load();
    } catch (e) { toast.error(apiError(e, "Adjustment failed")); }
    finally { setPromoBusy(false); }
  };

  if (loading) {
    return <DashboardLayout role="super_admin"><div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div></DashboardLayout>;
  }
  if (!data) return <DashboardLayout role="super_admin"><div className="p-10 text-center text-ink/50">Not found.</div></DashboardLayout>;

  const u = data.user;
  const w = data.wallet;
  return (
    <DashboardLayout role="super_admin">
      <div className="max-w-5xl">
        <Link to="/admin/users" className="text-sm text-ink/60 hover:text-ink inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to users</Link>

        {/* Identity card */}
        <Card className="mt-4 p-6 rounded-2xl">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-efgh-blue/10 text-efgh-blue grid place-items-center"><User className="w-7 h-7" /></div>
              <div>
                <h1 className="font-heading font-bold text-2xl text-ink" data-testid="profile-name">{u.full_name || "—"}</h1>
                <div className="text-sm text-ink/60">{u.email} · {u.phone || "no phone"}</div>
                <div className="mt-1 flex items-center gap-2">
                  <RoleBadge role={u.role} />
                  {u.lab_id && <span className="text-[10px] text-ink/50">Lab: <span className="font-mono">{u.lab_id.slice(0, 8)}…</span></span>}
                </div>
              </div>
            </div>
            <div className="text-right text-xs text-ink/50">
              Joined {(u.created_at || "").slice(0, 10)}<br/>
              Ref code <span className="font-mono">{u.referral_code || "—"}</span>
            </div>
          </div>
        </Card>

        {/* KPIs */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Wallet}       color="text-efgh-yellow" bg="bg-efgh-yellow/10" value={`${w.coins || 0} coins`} label="Wallet balance" testid="kpi-wallet" />
          <Kpi icon={FileText}     color="text-efgh-blue"   bg="bg-efgh-blue/10"   value={data.reports.count}       label="Reports"        testid="kpi-reports" />
          <Kpi icon={Gift}         color="text-efgh-orange" bg="bg-efgh-orange/10" value={(data.referrals.sent || []).length} label="Referrals sent" testid="kpi-referrals" />
          <Kpi icon={IndianRupee}  color="text-efgh-green"  bg="bg-efgh-green/10"  value={(data.recharges || []).length}       label="Recharges" testid="kpi-recharges" />
        </div>

        {/* Admin actions — promotional balance */}
        <Card className="mt-4 p-5 rounded-2xl border-l-4 border-efgh-orange" data-testid="admin-actions">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-heading font-semibold text-ink flex items-center gap-2"><Gift className="w-4 h-4 text-efgh-orange" /> Grant promotional balance</div>
              <p className="text-xs text-ink/60 mt-1">Credit or debit coins on this user&apos;s wallet. Logged in the wallet ledger with your admin ID.</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" data-testid="promo-amount" placeholder="Coins (e.g. 50)" value={promoAmt} onChange={(e) => setPromoAmt(e.target.value)} className="w-32" />
              <Input data-testid="promo-note" placeholder="Reason (optional)" value={promoNote} onChange={(e) => setPromoNote(e.target.value)} className="w-56" />
              <Button data-testid="promo-credit" onClick={() => grantPromo(1)} disabled={promoBusy || !promoAmt} className="bg-efgh-green hover:bg-efgh-green/90 text-white">
                {promoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Credit"}
              </Button>
              <Button data-testid="promo-debit" onClick={() => grantPromo(-1)} disabled={promoBusy || !promoAmt} variant="outline">
                Debit
              </Button>
            </div>
          </div>
        </Card>

        {/* Reports */}
        {data.reports.count > 0 && (
          <Section title={`Recent reports (${data.reports.count})`} icon={FileText}>
            <div className="text-[11px] text-ink/50 mb-1">Click a row to open the Smart Report.</div>
            <MiniTable
              rows={data.reports.recent}
              rowHref={(r) => `/report/${r.id}`}
              cols={[
                { key: "created_at", label: "Date", fmt: (v) => (v || "").slice(0, 10) },
                { key: "patient_name", label: "Patient" },
                { key: "family_member_name", label: "For" },
                { key: "package_name", label: "Package" },
                { key: "status", label: "Status", fmt: (v) => <span className="uppercase text-[10px] tracking-widest px-2 py-0.5 rounded bg-muted">{v}</span> },
              ]}
            />
          </Section>
        )}

        {/* Recharges */}
        {(data.recharges || []).length > 0 && (
          <Section title="Recharges" icon={IndianRupee}>
            <MiniTable rows={data.recharges} cols={[
              { key: "created_at", label: "Date", fmt: (v) => (v || "").slice(0, 10) },
              { key: "gateway", label: "Gateway", fmt: (v) => <span className="uppercase text-[10px] tracking-widest">{v}</span> },
              { key: "amount_paise", label: "Amount", fmt: (v, row) => `₹${(v ? v / 100 : row.amount_rupees) || 0}` },
              { key: "status", label: "Status" },
              { key: "coins", label: "Coins" },
            ]} />
          </Section>
        )}

        {/* Referrals */}
        {((data.referrals.sent || []).length > 0 || (data.referrals.received || []).length > 0) && (
          <Section title="Referrals" icon={Gift}>
            <div className="text-xs text-ink/60 mb-2">Sent: {(data.referrals.sent || []).length} · Received: {(data.referrals.received || []).length}</div>
            <MiniTable rows={data.referrals.sent} cols={[
              { key: "created_at", label: "Date", fmt: (v) => (v || "").slice(0, 10) },
              { key: "referee_user_id", label: "Referee", fmt: (v) => v ? v.slice(0, 8) + "…" : "—" },
              { key: "status", label: "Status" },
              { key: "reward_coins", label: "Reward" },
              { key: "reward_credited", label: "Paid", fmt: (v) => v ? "✅" : "—" },
            ]} />
          </Section>
        )}

        {/* Family */}
        {(data.family || []).length > 0 && (
          <Section title="Family members" icon={Building2}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {data.family.map((fm) => (
                <div key={fm.id} className="rounded-lg border border-border px-3 py-2">
                  <div className="font-semibold text-ink text-sm">{fm.full_name}</div>
                  <div className="text-[11px] text-ink/50">{fm.relation} · {fm.gender} · {fm.age}y</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Wallet ledger */}
        {(w.transactions || []).length > 0 && (
          <Section title="Wallet ledger" icon={Wallet}>
            <MiniTable rows={w.transactions} cols={[
              { key: "created_at", label: "Date", fmt: (v) => (v || "").slice(0, 10) },
              { key: "reason", label: "Reason" },
              { key: "delta", label: "Δ Coins", fmt: (v) => <span className={v >= 0 ? "text-success" : "text-danger"}>{v > 0 ? "+" : ""}{v}</span> },
              { key: "balance_after", label: "Balance" },
            ]} />
          </Section>
        )}
      </div>
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, color, bg, value, label, testid }) {
  return (
    <Card className="rounded-2xl p-4" data-testid={testid}>
      <div className={`w-9 h-9 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-4 h-4" /></div>
      <div className="mt-2 font-heading font-bold text-xl text-ink">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink/50 mt-1">{label}</div>
    </Card>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Card className="mt-4 p-5 rounded-2xl">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-muted grid place-items-center text-ink/70"><Icon className="w-3.5 h-3.5" /></div>
        <div className="font-heading font-semibold text-ink text-sm">{title}</div>
      </div>
      {children}
    </Card>
  );
}

function MiniTable({ rows, cols, rowHref }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-ink/50">No records.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-ink/50 font-heading">
          <tr>{cols.map((c) => <th key={c.key} className="text-left px-2 py-1">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const href = rowHref ? rowHref(r) : null;
            const rowCls = "border-t border-border/60" + (href ? " hover:bg-muted/40 cursor-pointer transition-colors" : "");
            return (
              <tr
                key={r.id || i}
                className={rowCls}
                onClick={href ? () => { window.location.href = href; } : undefined}
                data-testid={href ? `mini-row-${r.id || i}` : undefined}
              >
                {cols.map((c) => (
                  <td key={c.key} className="px-2 py-2 text-ink/80 whitespace-nowrap">{c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
